const __unsafeReactNodeBrand = Symbol('unsafeReactNode');

/**
 * Branded return type of {@link unsafeReactNode}.
 *
 * Config API text props are typed as `string | UnsafeReactNode` rather than
 * bare `string` so that the escape hatch is visible in the public API.
 * Plain strings satisfy the union without any casting — `UnsafeReactNode` only
 * matters when a `ReactNode` needs to pass through.
 */

/**
 * **Config API use only.** Blessed escape hatch for passing a `ReactNode`
 * where a Config API text prop expects `string | UnsafeReactNode`.
 *
 * Config API props are intentionally narrowed to `string` as the default for
 * governance: a `ReactNode` prop is a blank check — callers can pass anything,
 * including interactive elements (`<input>`, `<button>`) in places like dialog
 * titles or menu labels where the design system expects constrained content.
 * Narrowing to `string` keeps those surfaces auditable and gives the design
 * system team visibility into where the constraint is genuinely too tight.
 * This function exists for the cases where `string` is genuinely insufficient
 * — most commonly a JSX i18n component (`<FormattedMessage />`) or a label
 * that wraps an interactive element like a tooltip.
 *
 * The branded return type (`UnsafeReactNode`) means usages can't silently
 * spread to unrelated props — every call site is an explicit declaration of
 * intent and stays grepable. Clusters of `unsafeReactNode` calls on a single
 * component are a signal that it should natively support that use case.
 *
 * TODO: Find a first-class i18n solution so that translated content doesn't
 * require this escape hatch.
 *
 * @example i18n component
 * ```tsx
 * <FieldConfig label={unsafeReactNode(<FormattedMessage id="my.label" />)} />
 * ```
 *
 * @example Tooltip-wrapped label
 * ```tsx
 * <MenuItemConfig label={unsafeReactNode(<Tooltip><span>Label</span></Tooltip>)} />
 * ```
 */
export function unsafeReactNode(node) {
  return node;
}